/**
 * Experience Mode Types
 * 
 * Defines the structure for the contextual video tour system
 */

/** Layout breakpoints for responsive spot positioning */
export type SpotLayout = 'desktop' | 'tablet' | 'mobile';

export const LAYOUT_BREAKPOINTS = {
  desktop: 1280, // >= 1280px
  tablet: 768,   // 768px – 1279px
  mobile: 0,     // < 768px
} as const;

/** Position data for a single layout */
export interface LayoutPosition {
  x: number;  // document-relative CSS pixels from left
  y: number;  // document-relative CSS pixels from top
  refWidth?: number;  // document scrollWidth at save time
  refHeight?: number; // document scrollHeight at save time
}

export interface ExperienceSpot {
  id: string;
  pageId: string; // 'dashboard', 'netwerk', 'agenda', 'inbox', 'vibes', 'social-media'
  elementSelector?: string; // CSS selector for the UI element (optional, legacy)
  displayNumber: number; // Visual number shown to user (1, 2, 3...)
  
  // Multi-layout positions (admin-placed per breakpoint)
  layoutPositions?: {
    desktop?: LayoutPosition;
    tablet?: LayoutPosition;
    mobile?: LayoutPosition;
  };
  
  // Legacy fixed position (single position, backward compatible)
  fixedPosition?: {
    x: number; // document-relative pixels from left (CSS pixels, zoom-invariant)
    y: number; // document-relative pixels from top (CSS pixels, zoom-invariant)
    xPercent?: number; // document-width percentage (0-100, fallback)
    yPercent?: number; // document-height percentage (0-100, fallback)
    refWidth?: number; // document scrollWidth at save time (for smart scaling)
    refHeight?: number; // document scrollHeight at save time (for smart scaling)
  };
  
  // Position data (calculated at runtime, legacy)
  position?: {
    top: string;
    left: string;
    width: string;
    height: string;
  };
  
  iconPosition?: {
    top: string;
    left: string;
  };
  
  // Content
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl?: string;
  duration?: number; // in seconds
  
  // Metadata
  order: number;
  isRequired: boolean; // Must watch before marking page complete
  createdAt?: Date;
  updatedAt?: Date;
}

export interface TourProgress {
  userId: string;
  seenSpots: string[]; // Array of spot IDs that have been watched
  completedPages: string[]; // Array of page IDs fully completed
  totalSpots: number;
  percentage: number; // 0-100
  isComplete: boolean;
  completedAt?: Date;
  lastSeenDate?: Date;
}

export interface ExperienceModeState {
  isActive: boolean;
  currentPageId: string | null;
  spots: ExperienceSpot[];
  activeSpot: ExperienceSpot | null;
  isPlayingVideo: boolean;
  progress: TourProgress | null;
  isLoading: boolean;
}

export interface ExperienceSpotFormData {
  pageId: string;
  elementSelector: string;
  displayNumber: number;
  title: string;
  description: string;
  order: number;
  isRequired: boolean;
}
